/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {catchError, Observable, of, tap} from 'rxjs';
import {SpatialUtilService} from '../spatial-util.service';
import {FeatureApplyUpdateService} from '../feature-apply-update.service';
import {switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../../common/service/lookup.service';
import {RoadTypeLookup} from '../../../common/models/road-type.model';
import {ParcelTypeLookup} from '../../../common/models/parcel-type.model';
import {AlertModalService} from '../../../common/component/modal';
import {forkJoinDefaultIfEmpty} from '../../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class FeatureAssociationService {
    title = 'FeatureAssociationService';

    constructor(
        private alertModalService: AlertModalService,
        private eventBus: NgEventBus,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    updateBusinessFeatureAssociationChanges(layer: any, feature: any, removedChildren: any[], addedChildren: any[], childType: string) {
        console.debug(`${this.title} - updateBusinessFeatureAssociationChanges`);

        const attributeIds = [];
        const features = [];

        const childTypeIdsAttr = `${childType}ids`;

        if (feature.attributes[childTypeIdsAttr]) {
            attributeIds.push(...this.spatialUtilService.parseIdsAttr(feature.attributes[childTypeIdsAttr]));
            features.push(...this.spatialUtilService.getFeatures(childType).filter(f => attributeIds.includes(f.attributes.id)));
        }

        removedChildren.forEach(child => {
            const childIdx = attributeIds.indexOf(child.attributes.id);
            if (childIdx !== -1) {
                attributeIds.splice(childIdx, 1);

                const featureIdx = features.indexOf(child);
                if (featureIdx !== -1) {
                    features.splice(featureIdx, 1);
                }
            }
        });

        addedChildren.forEach(child => {
            const childIdx = attributeIds.indexOf(child.attributes.id);
            if (childIdx === -1) {
                attributeIds.push(child.attributes.id);
                features.push(child);
            }
        });

        feature.attributes[childTypeIdsAttr] = this.spatialUtilService.toIdsAttr(attributeIds);

        if (layer.id === 'roadFeatureLayer' || layer.id === 'lotFeatureLayer') {
            if (feature.attributes[childTypeIdsAttr] === null) {
                this.setTotalAreaAndGeometry(feature, []);
            } else {
                const polygons = this.spatialUtilService
                    .getPolygons()
                    .filter((p: any) => feature.attributes.polygonids && feature.attributes.polygonids.includes(`[${p.attributes.id}]`));
                this.setTotalAreaAndGeometry(feature, polygons);
            }
        }

        return this.featuresApplyUpdateService
            .updateFeaturesByType(layer, [feature])
            .pipe(tap(() => console.debug('Feature has been successfully updated!')));
    }

    resetPolygonIds(feature: any, polygons: any[]) {
        console.debug(`${this.title} - resetPolygonIds`);

        feature.attributes.polygonids = this.spatialUtilService.toIdsAttr(polygons.map(p => p.attributes.id));

        this.setTotalAreaAndGeometry(feature, polygons);
    }

    setTotalAreaAndGeometry(feature, polygons) {
        console.debug(`${this.title} - setTotalAreaAndGeometry`);

        feature.attributes.totalarea = this.spatialUtilService.sumPolygonStatedAreas(polygons);

        feature.geometry = this.spatialUtilService.polygonGeometryUnion(polygons.map(p => p.geometry));
    }

    saveInterestAssociation(id) {
        const feature = this.spatialUtilService.findById('interest', id);
        if (!feature) {
            return of(null);
        }
        const foundFeature = this.spatialUtilService.copyGraphic(feature);

        const selectedPolygonFeatures = this.spatialUtilService.getHighlightedPolygons();
        const selectedPolylineFeatures = this.spatialUtilService.getHighlightedPolylines();

        const currentAssociatedFeatures = this.getAllChildrenFeatures(foundFeature);
        const currentAssociatedPolygons = currentAssociatedFeatures.polygons;
        const currentAssociatedPolylines = currentAssociatedFeatures.polylines;

        const polygonsToBeRemoved = this.removePolygons(currentAssociatedPolygons, selectedPolygonFeatures);
        const polygonsToBeAdded = this.addPolygons(currentAssociatedPolygons, selectedPolygonFeatures);

        const layer = this.spatialUtilService.getInterestMapLayer();

        let observable: Observable<any> = of(null);

        if (polygonsToBeRemoved.length || polygonsToBeAdded.length) {
            observable = observable.pipe(
                switchMap(() => this.updatePolygonAssociationChanges(foundFeature, polygonsToBeRemoved, polygonsToBeAdded, 'interestids')),
                switchMap(() => this.updateBusinessFeatureAssociationChanges(layer, foundFeature, polygonsToBeRemoved, polygonsToBeAdded, 'polygon')),
                tap(() => {
                    if (polygonsToBeRemoved.length) {
                        this.eventBus.cast('deleteAnnotations', {
                            featureLayer: this.spatialUtilService.getPolygonMapLayer(),
                            features: polygonsToBeRemoved,
                            parentFeatureType: 'interest',
                            parentFeatureId: foundFeature.attributes.id
                        });
                    }
                    this.createAnnotations(foundFeature, polygonsToBeAdded, undefined, layer);
                })
            );
        }

        const polylinesToBeRemoved = this.removePolylines(currentAssociatedPolylines, selectedPolylineFeatures);
        const polylinesToBeAdded = this.addPolylines(currentAssociatedPolylines, selectedPolylineFeatures);

        if (polylinesToBeRemoved.length || polylinesToBeAdded.length) {
            observable = observable.pipe(
                switchMap(() => this.updatePolylineAssociationChanges(foundFeature, polylinesToBeRemoved, polylinesToBeAdded, 'interestids')),
                switchMap(() =>
                    this.updateBusinessFeatureAssociationChanges(layer, foundFeature, polylinesToBeRemoved, polylinesToBeAdded, 'polyline')
                ),
                switchMap(() => {
                    if (polylinesToBeRemoved.length) {
                        const polylinesToBeRemovedIds = polylinesToBeRemoved.map(p => p.attributes.id);
                        const annotationsToBeRemoved = this.spatialUtilService
                            .getAnnotations()
                            .filter(
                                (a: any) =>
                                    a.attributes.featuretype === 'interest' &&
                                    a.attributes.featureid === id &&
                                    a.attributes.childtype === 'polyline' &&
                                    polylinesToBeRemovedIds.includes(a.attributes.childid)
                            );
                        if (annotationsToBeRemoved.length) {
                            return this.spatialUtilService.applyEdits('annotation', null, null, annotationsToBeRemoved);
                        }
                    }
                    return of(null);
                }),
                tap(() => this.createAnnotations(foundFeature, [], polylinesToBeAdded, layer))
            );
        }

        return observable.pipe(switchMap(() => this.spatialUtilService.deleteInvalidInterestAnnotations()));
    }

    saveRoadAssociation(id: number) {
        let road = this.spatialUtilService.findById('road', id);
        if (!road) {
            return of(null);
        }
        road = this.spatialUtilService.copyGraphic(road);

        let observable: Observable<any> = of(null);

        const selectedPolygonFeatures = this.spatialUtilService.getHighlightedPolygons();

        this.spatialUtilService.geodesicAreas(selectedPolygonFeatures.map(p => p.geometry)).forEach((v, i) => {
            const attrs = selectedPolygonFeatures[i].attributes;
            if (!attrs.statedarea) {
                attrs.statedarea = `${Math.round(v)}`;
            }
        });

        const currentAssociatedFeatures = this.getAllChildrenFeatures(road);
        const currentAssociatedPolygons = currentAssociatedFeatures.polygons;

        const polygonsToBeRemoved = this.removePolygons(currentAssociatedPolygons, selectedPolygonFeatures);
        const polygonsToBeAdded = this.addPolygons(currentAssociatedPolygons, selectedPolygonFeatures);

        const selectedRoadPolygons = selectedPolygonFeatures.filter((p: any) => p.attributes.roadid && p.attributes.roadid !== id);
        const lotPolygons = selectedPolygonFeatures.filter((p: any) => p.attributes.lotid);

        const layer = this.spatialUtilService.getRoadMapLayer();

        const roadPolygonsToBeRemoved = [...polygonsToBeRemoved, ...selectedRoadPolygons];
        if (roadPolygonsToBeRemoved.length) {
            this.spatialUtilService.unhighlightFeatures('polygon', roadPolygonsToBeRemoved);
            observable = observable.pipe(switchMap(() => this.dissociatePolygonsFromRoads(roadPolygonsToBeRemoved)));
        }

        if (lotPolygons.length) {
            // Remove lot associations
            observable = observable.pipe(
                switchMap(() =>
                    forkJoinDefaultIfEmpty(lotPolygons.map((p: any) => this.saveLotDissociation(p.attributes.lotid, p.attributes.id, 'polygon')))
                )
            );
        }

        // Apply road associations
        if (polygonsToBeRemoved.length || polygonsToBeAdded.length) {
            observable = observable.pipe(
                switchMap(() => this.updatePolygonAssociationChanges(road, polygonsToBeRemoved, polygonsToBeAdded, 'roadid')),
                switchMap(() => this.updateBusinessFeatureAssociationChanges(layer, road, polygonsToBeRemoved, polygonsToBeAdded, 'polygon')),
                tap(() => {
                    if (polygonsToBeRemoved.length) {
                        this.eventBus.cast('deleteAnnotations', {
                            featureLayer: this.spatialUtilService.getPolygonMapLayer(),
                            features: polygonsToBeRemoved,
                            parentFeatureType: 'road'
                        });
                    }
                    this.createAnnotations(road, polygonsToBeAdded, undefined, layer);
                })
            );
        }

        return observable;
    }

    saveRoadDissociation(roadId: number, polygonsToRemoveIds: number[]) {
        const road = this.spatialUtilService.findById('road', roadId);
        const polygonsToRemove = this.spatialUtilService.getPolygons().filter((p: any) => polygonsToRemoveIds.includes(p.attributes.id));
        const polygonIds: number[] = this.spatialUtilService.parseIdsAttr(road.attributes.polygonids);
        const polygonIdsToKeep = polygonIds.filter(polygonId => !polygonsToRemoveIds.includes(polygonId));

        return this.updatePolygonAssociationChanges(road, polygonsToRemove, [], 'roadid').pipe(
            switchMap(() =>
                this.updateBusinessFeatureAssociationChanges(
                    this.spatialUtilService.getRoadMapLayer(),
                    this.spatialUtilService.copyGraphic(road),
                    polygonsToRemove,
                    [],
                    'polygon'
                )
            ),
            switchMap(() => {
                let annotationsToDelete = [];

                const roadTypeLookup = this.lookupService.getLookups('roadType').find((l: RoadTypeLookup) => l.id === road.attributes.type);
                if (roadTypeLookup && roadTypeLookup.code === 'ROAD_WIDENING') {
                    annotationsToDelete = this.spatialUtilService.toApplyEditsDeletes(
                        'annotation',
                        this.spatialUtilService
                            .getAnnotations()
                            .filter(
                                (a: any) =>
                                    a.attributes.featuretype === 'road' &&
                                    a.attributes.featureid === roadId &&
                                    a.attributes.childtype === 'polygon' &&
                                    polygonsToRemoveIds.includes(a.attributes.childid)
                            )
                    );
                } else if (polygonIdsToKeep.length === 0) {
                    annotationsToDelete = this.spatialUtilService.toApplyEditsDeletes(
                        'annotation',
                        this.spatialUtilService
                            .getAnnotations()
                            .filter((a: any) => a.attributes.featuretype === 'road' && a.attributes.featureid === roadId)
                    );
                }

                if (annotationsToDelete.length) {
                    return this.spatialUtilService.applyEdits('annotation', null, null, annotationsToDelete);
                }

                return of(null);
            })
        );
    }

    dissociatePolygonsFromRoads(polygons: any[]) {
        const roadPolygons = polygons.filter(p => p.attributes.roadid);
        if (roadPolygons.length === 0) {
            return of(null);
        }

        const roadPolygonIdsMap = roadPolygons.reduce((acc, p) => {
            const polygonRoadId = p.attributes.roadid;
            if (!acc[polygonRoadId]) {
                acc[polygonRoadId] = [];
            }
            acc[polygonRoadId].push(p.attributes.id);
            return acc;
        }, {});

        return forkJoinDefaultIfEmpty(Object.keys(roadPolygonIdsMap).map(k => this.saveRoadDissociation(parseInt(k), roadPolygonIdsMap[k])));
    }

    saveLotDissociation(lotId: number, childId: number, childType: string) {
        const child = this.spatialUtilService.findById(childType, childId);
        if (child) {
            this.spatialUtilService.unhighlightFeatures(childType, [child]);
            return this.applyLotDissociation(lotId, childId, childType);
        }
        return of(null);
    }

    applyLotDissociation(lotId: number, childId: number, childType: string) {
        console.debug(`${this.title} - applyLotDissociation`);

        const lot = this.spatialUtilService.findById('lot', lotId);
        const child = this.spatialUtilService.findById(childType, childId);

        if (lot && child) {
            return this.updateLotPolygonAssociationChanges(lot, [child], []).pipe(
                switchMap(() => this.updateLotAssociationChanges(lot, [child], [])),
                tap(() => {
                    this.eventBus.cast('deleteAnnotations', {
                        featureLayer: this.spatialUtilService.getMapFeatureLayer(childType),
                        features: [child],
                        parentFeatureType: 'lot'
                    });
                    this.eventBus.cast('featuresUpdated');
                    return window.setTimeout(() => this.eventBus.cast('updateAnnotation', {feature: lot, featureType: 'lot'}), 500);
                })
            );
        }

        return of(null);
    }

    updateLotPolygonAssociationChanges(lot: any, removedPolygons: any[], addedPolygons: any[]) {
        console.debug(`${this.title} - updatePolygonAssociationChanges`);

        removedPolygons.forEach(polygon => (polygon.attributes.lotid = null));

        const parcelTypeLookups: ParcelTypeLookup[] = this.lookupService.getLookups('parcelType');
        addedPolygons.forEach(polygon => {
            polygon.attributes.lotid = lot.attributes.id;
            // update polygon types to lot type
            polygon.attributes.type = Number(parcelTypeLookups.find((parcelType: any) => parcelType.id === lot.attributes.type).validPolygonTypes);
        });

        return this.spatialUtilService.applyEdits('polygon', null, [...removedPolygons, ...addedPolygons], null);
    }

    updateLotAssociationChanges(lot: any, removedPolygons: any[], addedPolygons: any[]) {
        console.debug(`${this.title} - updateLotAssociationChanges`);

        const polygonIds: number[] = this.spatialUtilService.parseIdsAttr(lot.attributes.polygonids);
        const polygonIdsToRemove = removedPolygons.map(p => p.attributes.id);
        const polygonIdsToAdd = addedPolygons.map(p => p.attributes.id);
        const newPolygonIds = [...polygonIds.filter(id => !polygonIdsToRemove.includes(id)), ...polygonIdsToAdd].filter(
            (v, i, a) => a.indexOf(v) === i
        );

        lot.attributes.polygonids = this.spatialUtilService.toIdsAttr(newPolygonIds);

        const polygons = newPolygonIds.length
            ? this.spatialUtilService.getPolygons().filter((p: any) => newPolygonIds.includes(p.attributes.id))
            : [];
        this.setTotalAreaAndGeometry(lot, polygons);

        return this.applyLotUpdates([lot]);
    }

    private applyLotUpdates(lots: any[]) {
        const logPrefix = `${this.title} - applyLotUpdates`;
        console.debug(logPrefix);

        return this.spatialUtilService.applyEdits('lot', null, lots, null).pipe(
            switchMap((res: any) => {
                console.debug(`${logPrefix} - updates:`, res.updates);

                if (res.updates[0].success) {
                    console.debug(`${logPrefix} - lots updated successfully`);

                    this.spatialUtilService.unhighlightFeatures(
                        'lot',
                        lots.filter(l => l.attributes.totalarea === 0)
                    );

                    return this.spatialUtilService.getMapFeatureLayer('lot').applyEdits(null, lots, null);
                } else {
                    this.alertModalService.createAlertMessage(
                        `Error updating lot features: ${(res.updates[0].error && res.updates[0].error.message) || ''}`,
                        'Ok'
                    );
                }
            }),
            catchError(err => this.alertModalService.createAlertMessage('Error updating lot features. Error : ' + err, 'Ok'))
        );
    }

    private createAnnotations(feature: any, polygons: any[], polylines: any[], layer: any) {
        const allAnnotations = this.spatialUtilService.getAnnotations();

        if (layer.id === 'interestFeatureLayer') {
            this.eventBus.cast('createInterestAnnotation', {
                feature: feature,
                featureType: 'interest',
                polygons: polygons,
                polylines: polylines,
                allAnnotations: allAnnotations
            });
        }
        if (layer.id === 'roadFeatureLayer') {
            this.eventBus.cast('createRoadAnnotation', {
                feature: feature,
                featureType: 'road',
                polygons: polygons,
                polylines: undefined,
                allAnnotations: allAnnotations
            });
        }
    }

    private getAllChildrenFeatures(feature: any) {
        console.debug(`${this.title} - getAllChildrenFeatures`);

        const allChildren = {polygons: [], polylines: []};

        if (feature) {
            if (feature.attributes.polygonids) {
                const polygonIds = this.spatialUtilService.parseIdsAttr(feature.attributes.polygonids);
                allChildren.polygons = this.spatialUtilService.getPolygons().filter((p: any) => polygonIds.includes(p.attributes.id));
            }

            if (feature.attributes.polylineids) {
                const polylineIds = this.spatialUtilService.parseIdsAttr(feature.attributes.polylineids);
                allChildren.polylines = this.spatialUtilService.getPolylines().filter(p => polylineIds.includes(p.attributes.id));
            }
        }

        return allChildren;
    }

    private removePolygons(currentAssociatedPolygons: any[], selectedPolygons: any[]) {
        console.debug(`${this.title} - removePolygons`);

        return currentAssociatedPolygons.filter(
            (polygon: any) => !selectedPolygons.some(selectedPolygon => selectedPolygon.attributes.id === polygon.attributes.id)
        );
    }

    private addPolygons(currentAssociatedPolygons: any[], selectedPolygons: any[]) {
        console.debug(`${this.title} - addPolygons`);

        return selectedPolygons.filter(
            (polygon: any) => !currentAssociatedPolygons.some(associatedPolygon => associatedPolygon.attributes.id === polygon.attributes.id)
        );
    }

    private removePolylines(currentAssociatedPolylines: any[], selectedPolylines: any[]) {
        console.debug(`${this.title} - removePolylines`);

        return currentAssociatedPolylines.filter(
            (polyline: any) => !selectedPolylines.some(selectedPolyline => selectedPolyline.attributes.id === polyline.attributes.id)
        );
    }

    private addPolylines(currentAssociatedPolylines: any[], selectedPolylines: any[]) {
        console.debug(`${this.title} - addPolylines`);

        return selectedPolylines.filter(
            (polyline: any) => !currentAssociatedPolylines.some(associatedPolyline => associatedPolyline.attributes.id === polyline.attributes.id)
        );
    }

    updatePolygonAssociationChanges(feature: any, removedPolygons: any[], addedPolygons: any[], idName: string) {
        console.debug(`${this.title} - updatePolygonAssociationChanges`);

        removedPolygons.forEach(p => {
            if (idName === 'interestids') {
                p.attributes[idName] = this.spatialUtilService.updateIdsAttr(p.attributes[idName], feature.attributes.id, true);
            } else {
                p.attributes[idName] = null;
            }
        });

        const roadTypeLookupList: RoadTypeLookup[] = this.lookupService.getLookups('roadType');

        addedPolygons.forEach(p => {
            if (idName === 'interestids') {
                p.attributes[idName] = this.spatialUtilService.updateIdsAttr(p.attributes[idName], feature.attributes.id, false);
            } else {
                p.attributes[idName] = feature.attributes.id;
            }

            if (idName === 'roadid') {
                p.attributes.type = roadTypeLookupList.find(roadType => roadType.id === feature.attributes.type).validPolygonTypeCode;
            }
        });

        return this.spatialUtilService.applyEdits('polygon', null, [...removedPolygons, ...addedPolygons], null);
    }

    updatePolylineAssociationChanges(feature: any, removedPolylines: any[], addedPolylines: any[], idName: string) {
        console.debug(`${this.title} - updatePolyLineAssociationChanges`);

        removedPolylines.forEach((polyline: any) => {
            polyline.attributes[idName] = this.spatialUtilService.updateIdsAttr(polyline.attributes[idName], feature.attributes.id, true);
        });

        addedPolylines.forEach((polyline: any) => {
            polyline.attributes[idName] = this.spatialUtilService.updateIdsAttr(polyline.attributes[idName], feature.attributes.id, false);
        });

        return this.spatialUtilService.applyEdits('line', null, [...removedPolylines, ...addedPolylines], null);
    }
}
